<div class="bg-gray-900 text-white rounded-lg overflow-hidden">
  <button
    type="button"
    class="w-full p-4 flex justify-between items-center cursor-pointer select-none border-b border-gray-700 text-left"
    (click)="expanded = !expanded"
  >
    <span class="flex items-center gap-4">
      <span class="font-bold">{{ 'project.steps.rfd.evaluation.summary.title' | transloco }}</span>
      @if (leader()) {
        <span
          class="flex items-center gap-1 text-xs bg-green-900 text-green-300 px-2 py-1 rounded-sm border border-green-700"
        >
          <span class="font-bold">Leader:</span>
          <span>{{ leader() }}</span>
        </span>
      }
    </span>
    <span class="text-xs text-gray-400">
      {{ (expanded ? 'common.hide' : 'common.show') | transloco }}
      <i class="pi ml-1" [ngClass]="expanded ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
    </span>
  </button>

  @if (expanded) {
    <div class="p-4 flex flex-col gap-4">
      @for (score of providerScores(); track score) {
        <div class="bg-gray-800 rounded-sm p-4 border border-gray-700">
          <div class="flex justify-between items-start mb-4">
            <div>
              <div class="font-bold text-lg text-blue-300 flex items-center gap-2">
                @if (getProviderName(score.toolId); as providerName) {
                  <span>{{ providerName }}</span>
                }
                @if (getProviderName(score.toolId) && getHrToolName(score.toolId)) {
                  <span class="text-gray-400"> | </span>
                }
                <span>{{ getHrToolName(score.toolId) }}</span>
              </div>
            </div>
            <div class="text-right">
              <div class="relative inline-block group">
                <div
                  class="text-2xl font-bold"
                  [ngClass]="{
                    'text-green-400': score.overall >= getThresholdScore(3),
                    'text-yellow-400':
                      score.overall >= getThresholdScore(2) && score.overall < getThresholdScore(3),
                    'text-red-400': score.overall < getThresholdScore(2),
                  }"
                >
                  {{ score.overall | appNumber: '1.1-1' }}
                  ({{ (score.overall / getMaxScore()) * 100 | appNumber: '1.0-0' }}%)
                </div>
                <div
                  class="absolute right-0 mt-2 w-80 max-w-[90vw] rounded-lg border border-gray-700 bg-gray-900 p-3 text-xs text-gray-200 shadow-lg opacity-0 invisible group-hover:visible group-hover:opacity-100 transition"
                >
                  <div class="text-xs font-semibold text-gray-300 mb-2">Overall Score</div>
                  <div class="space-y-1">
                    @for (detail of getOverallBreakdown(score); track detail) {
                      <div class="flex justify-between gap-2">
                        <div
                          class="truncate"
                          [title]="
                            detail.subcriteria.translations
                              | directusTranslation: currentLang() : '' : 'name'
                          "
                        >
                          {{
                            detail.subcriteria.translations
                              | directusTranslation: currentLang() : '' : 'name'
                          }}
                        </div>
                        <div class="text-right whitespace-nowrap text-gray-300">
                          {{ detail.score | appNumber: '1.1-1' }} ×
                          {{ detail.weight * 100 | appNumber: '1.2-2' }}% =
                          {{ detail.contribution | appNumber: '1.2-2' }}
                        </div>
                      </div>
                    }
                  </div>
                  <div
                    class="mt-2 pt-2 border-t border-gray-700 flex justify-between font-semibold"
                  >
                    <span>Total</span>
                    <span>
                      {{ score.overall | appNumber: '1.1-1' }}
                      ({{ (score.overall / getMaxScore()) * 100 | appNumber: '1.0-0' }}%)
                    </span>
                  </div>
                </div>
              </div>
              <div class="text-xs text-gray-500">Overall Score</div>
            </div>
          </div>

          <!-- Subcriteria -->
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            @for (subcriteria of data().subcriteria; track subcriteria) {
              <div class="bg-gray-900 rounded-sm p-2 border-l-4 border-blue-500">
                <div
                  class="text-xs text-gray-400 mb-1 truncate"
                  [title]="
                    (subcriteria.translations | directusTranslation: currentLang() : '' : 'name') +
                    ' (' +
                    ('common.weight' | transloco) +
                    ': ' +
                    ((subcriteria.weight ?? 0) * 100 | appNumber: '1.0-0') +
                    '%)'
                  "
                >
                  {{ subcriteria.translations | directusTranslation: currentLang() : '' : 'name' }}
                  ({{ 'common.weight' | transloco }}:
                  {{ (subcriteria.weight ?? 0) * 100 | appNumber: '1.2-2' }}%)
                </div>
                <div
                  class="font-bold"
                  [ngClass]="{
                    'text-green-400':
                      (score.subcriteriaScores.get(subcriteria.id) || 0) >= getThresholdScore(3),
                    'text-yellow-400':
                      (score.subcriteriaScores.get(subcriteria.id) || 0) >= getThresholdScore(2) &&
                      (score.subcriteriaScores.get(subcriteria.id) || 0) < getThresholdScore(3),
                    'text-red-400':
                      (score.subcriteriaScores.get(subcriteria.id) || 0) < getThresholdScore(2),
                  }"
                >
                  {{ score.subcriteriaScores.get(subcriteria.id) || 0 | appNumber: '1.1-1' }}
                  ({{
                    ((score.subcriteriaScores.get(subcriteria.id) || 0) / getMaxScore()) * 100
                      | appNumber: '1.0-0'
                  }}%)
                </div>
              </div>
            }
          </div>

          <!-- Agenda Items -->
          <div class="space-y-2">
            <div class="text-xs font-bold text-gray-500 uppercase">Scenario Performance</div>
            <div class="flex gap-2 overflow-x-auto pb-2">
              @for (item of data().agendaItems; track item) {
                <div class="bg-gray-900 rounded-sm p-2 flex-1 min-w-[150px] border border-gray-700">
                  <div
                    class="text-xs text-gray-400 mb-1 truncate"
                    [title]="item.translations | directusTranslation: currentLang() : '' : 'topic'"
                  >
                    {{ item.translations | directusTranslation: currentLang() : '' : 'topic' }}
                  </div>
                  <div
                    class="font-bold"
                    [ngClass]="{
                      'text-green-400':
                        (score.agendaScores.get(item.id) || 0) >= getThresholdScore(3),
                      'text-yellow-400':
                        (score.agendaScores.get(item.id) || 0) >= getThresholdScore(2) &&
                        (score.agendaScores.get(item.id) || 0) < getThresholdScore(3),
                      'text-red-400': (score.agendaScores.get(item.id) || 0) < getThresholdScore(2),
                    }"
                  >
                    {{ score.agendaScores.get(item.id) || 0 | appNumber: '1.1-1' }}
                    ({{
                      ((score.agendaScores.get(item.id) || 0) / getMaxScore()) * 100
                        | appNumber: '1.0-0'
                    }}%)
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
      }
    </div>
  }
</div>
